import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Box, Tabs } from "@mantine/core"
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Container, LayoutStandard } from "components";
import { AuthorityConstants, RouteConstants, ServiceConstants } from "constant";
import { UserService } from "services";
import { displayClasses } from "styles";
import { AppUtils, UserUtils } from "utils";

import ClientsList from "../../clients/list/list";
import { IRootState } from "config/redux";

const { ROLE_CR_C } = AuthorityConstants;
const { REQUEST_USERS_CLIENTS_STATS_ID } = ServiceConstants;

const TABS = {
  ALL: 'all',
  REGISTRATION_UNCOMPLETED: 'registration_uncompleted',
  REGISTRATION_COMPLETED: 'registration_completed',
  REQUEST_STARTED: 'request_started',
  REQUEST_WITHOUT_DOCUMENTS: 'request_without_documents',
  REQUEST_WITH_DOCUMENTS: 'request_with_documents'
}

const CLientsMyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_STATS_ID]);
  const [activeTab, setActiveTab] = useState<string | null>(TABS.ALL);
  const [stats, setStats] = useState<any>({});
  const prevSource = useRef<string | undefined>(undefined);

  const fetch = useCallback((page: number, search: string | undefined, phase: string, source?: string) => {
    if (phase !== TABS.ALL.toUpperCase() && prevSource.current !== undefined && prevSource.current !== source) {
      setActiveTab(TABS.ALL);
      prevSource.current = source;
      return;
    }
    if (source !== prevSource.current) {
      UserService.userClientsStats(source, true);
    }
    prevSource.current = source;
    UserService.userClients({ page: page, search: search, phase: phase, source: source, agent: true });
  }, [activeTab]);

  const newAllowed = useMemo(() => UserUtils.hasAuthority(ROLE_CR_C), []);
  const addNew = useCallback(() => navigate(RouteConstants.ROUTE_CLIENTS_NEW), []);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USERS_CLIENTS_STATS_ID, setStats);
  }, [serviceObject]);

  return (
    <LayoutStandard
      authority={AuthorityConstants.ROLE_CR}
      title={AppUtils.composeTabTitle(t('clients.list.title'))}
      >
      <Container>
        <Tabs keepMounted={false} variant={'pills'} value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value={TABS.ALL} className={displayClasses.tabList}>
              {t('clients.list.tab.' + TABS.ALL)}
              <BadgeNumber value={stats[TABS.ALL.toUpperCase()]} />
            </Tabs.Tab>
            <Tabs.Tab value={TABS.REGISTRATION_UNCOMPLETED} className={displayClasses.tabList}>
              {t('clients.list.tab.' + TABS.REGISTRATION_UNCOMPLETED)}
              <BadgeNumber value={stats[TABS.REGISTRATION_UNCOMPLETED.toUpperCase()]} />
            </Tabs.Tab>
            <Tabs.Tab value={TABS.REGISTRATION_COMPLETED} className={displayClasses.tabList}>
              {t('clients.list.tab.' + TABS.REGISTRATION_COMPLETED)}
              <BadgeNumber value={stats[TABS.REGISTRATION_COMPLETED.toUpperCase()]} />
            </Tabs.Tab>
            <Tabs.Tab value={TABS.REQUEST_STARTED} className={displayClasses.tabList}>
              {t('clients.list.tab.' + TABS.REQUEST_STARTED)}
              <BadgeNumber value={stats[TABS.REQUEST_STARTED.toUpperCase()]} />
            </Tabs.Tab>
            <Tabs.Tab value={TABS.REQUEST_WITHOUT_DOCUMENTS} className={displayClasses.tabList}>
              {t('clients.list.tab.' + TABS.REQUEST_WITHOUT_DOCUMENTS)}
              <BadgeNumber value={stats[TABS.REQUEST_WITHOUT_DOCUMENTS.toUpperCase()]} />
            </Tabs.Tab>
            <Tabs.Tab value={TABS.REQUEST_WITH_DOCUMENTS} className={displayClasses.tabList}>
              {t('clients.list.tab.' + TABS.REQUEST_WITH_DOCUMENTS)}
              <BadgeNumber value={stats[TABS.REQUEST_WITH_DOCUMENTS.toUpperCase()]} />
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={TABS.ALL} className={displayClasses.tabContent}><ClientsList fetch={fetch} addNew={newAllowed ? addNew : undefined} phase={TABS.ALL.toUpperCase()} /></Tabs.Panel>
          <Tabs.Panel value={TABS.REGISTRATION_UNCOMPLETED} className={displayClasses.tabContent}><ClientsList fetch={fetch} addNew={newAllowed ? addNew : undefined} phase={TABS.REGISTRATION_UNCOMPLETED.toUpperCase()} /></Tabs.Panel>
          <Tabs.Panel value={TABS.REGISTRATION_COMPLETED} className={displayClasses.tabContent}><ClientsList fetch={fetch} addNew={newAllowed ? addNew : undefined} phase={TABS.REGISTRATION_COMPLETED.toUpperCase()} /></Tabs.Panel>
          <Tabs.Panel value={TABS.REQUEST_STARTED} className={displayClasses.tabContent}><ClientsList fetch={fetch} addNew={newAllowed ? addNew : undefined} phase={TABS.REQUEST_STARTED.toUpperCase()} /></Tabs.Panel>
          <Tabs.Panel value={TABS.REQUEST_WITHOUT_DOCUMENTS} className={displayClasses.tabContent}><ClientsList fetch={fetch} addNew={newAllowed ? addNew : undefined} phase={TABS.REQUEST_WITHOUT_DOCUMENTS.toUpperCase()} /></Tabs.Panel>
          <Tabs.Panel value={TABS.REQUEST_WITH_DOCUMENTS} className={displayClasses.tabContent}><ClientsList fetch={fetch} addNew={newAllowed ? addNew : undefined} phase={TABS.REQUEST_WITH_DOCUMENTS.toUpperCase()} /></Tabs.Panel>
        </Tabs>
      </Container>
    </LayoutStandard>
  )
}

const BadgeNumber = ({ value }: { value: number | undefined }) => (
  value === undefined
    ? <></>
    : <Box style={{ marginLeft: 8, position: 'relative', bottom: 2, display: 'inline' }}>
        <Badge size={'sm'} bg={'none'} c={'gray'} style={{ borderColor: 'gray' }}>{value}</Badge>
      </Box>
)

export default CLientsMyPage;